import { useState } from "react";
import { ShieldAlert, Crosshair, BookOpen, ListChecks, ChevronDown, Target, Layers, Eye, Wrench } from "lucide-react";

const tacticColor = "text-status-critical";

function Section({ icon: Icon, title, accent, children }) {
  return (
    <div className="px-4 py-3 border-b border-border/40 last:border-0">
      <div className={`flex items-center gap-2 mb-2 label-ot ${accent || ""}`}>
        <Icon className="w-3.5 h-3.5" />{title}
      </div>
      {children}
    </div>
  );
}

function MitreChip({ t }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-status-critical/30 rounded-sm bg-status-critical/5 overflow-hidden" data-testid={`mitre-${t.id}`}>
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-2 px-2.5 py-1.5 text-left hover:bg-status-critical/10">
        <span className="font-mono text-[11px] font-semibold text-status-critical">{t.id}</span>
        <span className="text-xs flex-1">{t.name}</span>
        <span className="font-mono text-[9px] text-muted-foreground hidden sm:inline">{t.tactic}</span>
        <ChevronDown className={`w-3.5 h-3.5 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-2.5 pb-2.5 pt-1 space-y-1.5 border-t border-status-critical/20">
          <div className="flex gap-2 text-[11px]"><Eye className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" /><span><span className="text-primary font-semibold">Detection:</span> <span className="text-foreground/80">{t.detection}</span></span></div>
          <div className="flex gap-2 text-[11px]"><Wrench className="w-3.5 h-3.5 text-status-normal shrink-0 mt-0.5" /><span><span className="text-status-normal font-semibold">Mitigation:</span> <span className="text-foreground/80">{t.mitigation}</span></span></div>
        </div>
      )}
    </div>
  );
}

export default function ScenarioBriefing({ attempt }) {
  const [collapsed, setCollapsed] = useState(true);
  const isSecurity = attempt.category === "security";
  const mitre = attempt.mitre_techniques || [];
  const iec = attempt.iec62443 || [];
  const chain = attempt.attack_chain || [];
  const learn = attempt.learning_objectives || [];

  return (
    <div className="bg-card border rounded-md mb-4" style={{ borderColor: isSecurity ? "rgba(255,59,48,0.3)" : "rgba(0,122,255,0.3)" }} data-testid="scenario-briefing">
      <div className="px-4 py-2.5 flex items-center justify-between bg-popover border-b border-border/60">
        <span className={`flex items-center gap-2 label-ot ${isSecurity ? "text-status-critical" : "text-primary"}`}>
          {isSecurity ? <ShieldAlert className="w-3.5 h-3.5" /> : <BookOpen className="w-3.5 h-3.5" />}
          {isSecurity ? "Threat Briefing" : "Mission Briefing"}
        </span>
        <button data-testid="toggle-briefing" onClick={() => setCollapsed((c) => !c)} className="text-muted-foreground hover:text-foreground flex items-center gap-1 text-xs">
          {collapsed ? "Expand" : "Collapse"} <ChevronDown className={`w-4 h-4 transition-transform ${collapsed ? "" : "rotate-180"}`} />
        </button>
      </div>

      {!collapsed && (
        <div>
          {attempt.context && (
            <Section icon={Target} title={isSecurity ? "Threat Context" : "Context"} accent={isSecurity ? "text-status-critical" : ""}>
              <p className="text-sm text-foreground/85 leading-relaxed">{attempt.context}</p>
            </Section>
          )}

          {chain.length > 0 && (
            <Section icon={Crosshair} title="Attack Chain" accent="text-status-critical">
              <ol className="space-y-1.5">
                {chain.map((step, i) => (
                  <li key={i} className="flex gap-2.5 text-xs">
                    <span className="font-mono text-[10px] w-5 h-5 rounded-sm bg-status-critical/15 text-status-critical flex items-center justify-center shrink-0 font-semibold">{i + 1}</span>
                    <span className="text-foreground/80 pt-0.5">{step}</span>
                  </li>
                ))}
              </ol>
            </Section>
          )}

          {mitre.length > 0 && (
            <Section icon={Layers} title="MITRE ATT&CK for ICS" accent="text-status-critical">
              <div className="grid sm:grid-cols-2 gap-1.5">
                {mitre.map((t) => <MitreChip key={t.id} t={t} />)}
              </div>
              <p className="text-[10px] text-muted-foreground mt-1.5">Click a technique to see detection &amp; mitigation guidance.</p>
            </Section>
          )}

          {iec.length > 0 && (
            <Section icon={ShieldAlert} title="IEC-62443 Mapping" accent="text-primary">
              <div className="flex flex-wrap gap-1.5">
                {iec.map((f) => (
                  <div key={f.fr} data-testid={`iec-${f.fr}`} className="border border-primary/30 bg-primary/5 rounded-sm px-2.5 py-1.5 max-w-xs">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-[11px] font-semibold text-primary">{f.fr}</span>
                      <span className="text-xs">{f.name}</span>
                      {f.sl != null && <span className="font-mono text-[9px] px-1.5 py-0.5 rounded-sm bg-primary/15 text-primary ml-auto">SL{f.sl}</span>}
                    </div>
                    {f.note && <p className="text-[10px] text-muted-foreground mt-1">{f.note}</p>}
                  </div>
                ))}
              </div>
            </Section>
          )}

          {learn.length > 0 && (
            <Section icon={Target} title="Learning Objectives">
              <ul className="space-y-1">
                {learn.map((l, i) => <li key={i} className="text-xs text-foreground/80 flex gap-2"><span className="text-primary">▹</span>{l}</li>)}
              </ul>
            </Section>
          )}

          {(attempt.instructions || []).length > 0 && (
            <Section icon={ListChecks} title="Operator Tasks">
              <ol className="list-decimal list-inside space-y-1 text-xs text-foreground/85 marker:text-primary marker:font-mono">
                {attempt.instructions.map((ins, i) => <li key={i}>{ins}</li>)}
              </ol>
            </Section>
          )}
        </div>
      )}
    </div>
  );
}
